import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import GatewaySelectionDialog from "../Common/PaymentGatewaySelectionDialog";
import AutoDebitIntervalDialog, {
  getAvailableAutoDebitIntervals,
} from "../Common/AutoDebitIntervalDialog";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { createOfflineOrderRequest, resetOfflineOrderState } from "../../redux/OfflineOrder/offlineOrderSlice";
import {
  getPaymentPlatform,
  normalizeGateway,
  savePaymentMeta,
  startPaymentFromOrderResponse,
} from "../Common/PaymentGatewayMethods";
import { toast } from "react-toastify";
import {
  createOnlineOrderRequest,
  resetOnlineOrderState,
} from "../../redux/OnlineOrder/onlineOrderSlice";
import { gstHelper } from "../../Utils/GstCalculationResult";
import { ThemeContext } from "@emotion/react";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";

const OrderSummary = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const offlineOrder = useSelector((state) => state.offline_order);
  const onlineOrder = useSelector((state) => state.onlineOrder);
  const { customer: fetchedCustomer } = useSelector(
    (state) => state.profile || {},
  );
  const customer = fetchedCustomer || {};

  const metal = state?.metal || {};
  console.log("metal", metal);
  const grams = Number(state?.grams) || 0;
  const amount = Number(state?.amount) || 0;
  const reduxAppConfig = useSelector((s) => s.dashboard?.appConfig || {});
  const appConfig = state?.appConfig || reduxAppConfig || {};
  const paymentGateways = appConfig?.paymentGateways || [];
  const featureToggle = appConfig?.featureToggle || {};
  const buyAutoDebitEnabled = Boolean(
    featureToggle.buy_auto_debit ||
      featureToggle.Buy_Auto_Debit ||
      featureToggle.buyAutoDebit,
  );
  const decimal = appConfig?.decimalPrecision ?? 4;
  const taxtype = appConfig?.taxtype ?? "GST";
  const jewellerState = appConfig?.jewellerState;
  const customerState = customer.address?.[0]?.state || "";

  const cgstPercent = metal?.cgst ?? appConfig?.gst?.cgst ?? 0;
  const sgstPercent = metal?.sgst ?? appConfig?.gst?.sgst ?? 0;
  const igstPercent = metal?.tax ?? appConfig?.gst?.igst ?? 0;
  const cessPercent = metal?.cess ?? 0;
  const isSameState =
    customerState?.trim().toLowerCase() === jewellerState?.trim().toLowerCase();
    

  const gstResult = gstHelper({
    finalWeight: grams,
    rate: amount / grams,
    igst: igstPercent,
    cgst: cgstPercent,
    sgst: sgstPercent,
    taxRate: igstPercent,
    cess: cessPercent,
    taxType: taxtype,
    customerState,
    jewellerState,
  });

  const { cgst, sgst, tax, metalCost, finalAmount } = gstResult;

  // const cgst = parseFloat(((amount * cgstPercent) / 100).toFixed(decimal));
  // const sgst = parseFloat(((amount * sgstPercent) / 100).toFixed(decimal));
  // const tax = cgst + sgst;

  // const totalAmount = parseFloat((amount + tax).toFixed(decimal));

  const paymentModeEnabled = appConfig?.paymentModeEnabled || {};

  const [openGatewayDialog, setOpenGatewayDialog] = useState(false);
  const [openIntervalDialog, setOpenIntervalDialog] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [autoDebit, setAutoDebit] = useState(false);
  const [autoDebitInterval, setAutoDebitInterval] = useState("DAY");
  const [loading, setLoading] = useState(false);

  const metalColor =
    metal.metalName === "Gold"
      ? "linear-gradient(135deg, #FFD700, #DAA520)"
      : metal.metalName === "Silver"
        ? "linear-gradient(135deg, #C0C0C0, #E0E0E0)"
        : "linear-gradient(135deg, #B0BEC5, #ECEFF1)";

  const proceedWithGateways = async (interval = autoDebitInterval) => {
    if (paymentGateways.length === 1) {
      const gateway = normalizeGateway(paymentGateways[0]);
      setSelectedGateway(gateway);
      await createOrderAndPay(gateway, interval);
      return;
    }
    setLoading(false);
    setOpenGatewayDialog(true);
  };

  const handlePayOnline = async () => {
    if (paymentGateways.length === 0) {
      toast.warn("No payment gateways configured!");
      return;
    }

    setLoading(true);

    if (autoDebit) {
      const intervals = getAvailableAutoDebitIntervals(appConfig?.buyAutoDebit);
      if (!intervals.length) {
        toast.error("No auto debit interval is enabled. Please contact administrator.");
        setLoading(false);
        return;
      }
      setLoading(false);
      setOpenIntervalDialog(true);
      return;
    }

    await proceedWithGateways();
  };

  const handleIntervalSelect = async (interval) => {
    setOpenIntervalDialog(false);
    if (!interval) {
      setLoading(false);
      return;
    }
    setAutoDebitInterval(interval);
    setLoading(true);
    await proceedWithGateways(interval);
  };

  const handleGatewaySelect = async (gateway) => {
    setOpenGatewayDialog(false);
    if (!gateway) {
      setLoading(false);
      return;
    }
    const normalized = normalizeGateway(gateway);
    setSelectedGateway(normalized);
    await createOrderAndPay(normalized);
  };

  const createOrderAndPay = async (gateway, interval = autoDebitInterval) => {
    try {
      setLoading(true);
      const normalizedGateway = normalizeGateway(gateway);
      const sipDay = autoDebit ? new Date().getDate() : 0;

      const payLoad = {
        weight: grams,
        metalName: metal?.metalName,
        orderType: "Buy",
        final_amount: Number(finalAmount.toFixed(decimal)),
        tax: Number(tax.toFixed(decimal)),
        noOfMonths: 0,
        Id: "",
        sipDay,
        buyType: "Weight",
        purity: metal?.purity,
        transactionMode: normalizedGateway,
        platform: getPaymentPlatform(),
        installment_amount: 0,
        planid: "",
        moneysip: false,
        tag: "",
        x_api_version: "2022-09-01",
        jeweller: process.env.REACT_APP_JEWELLER_ID,
        autodebit: Boolean(autoDebit),
        interval: autoDebit ? interval : "DAY",
      };

      savePaymentMeta(
        {
          grams,
          metal,
          amount,
          totalAmount: finalAmount,
          tax,
          selectedGateway: normalizedGateway,
          orderType: payLoad.orderType,
          buyType: payLoad.buyType,
          planid: "",
          moneysip: false,
          autodebit: Boolean(autoDebit),
        },
        { replace: true },
      );
      dispatch(resetOnlineOrderState());
      dispatch(createOnlineOrderRequest(payLoad));
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to create order");
      setLoading(false);
    }
  };

  // const verifyAfterPayment = async (orderId) => {
  //   console.log("verify payment api call");
  //   try {
  //     const verifyPayload = {
  //       signature: "",
  //       orderId: orderId,
  //       data: "",
  //       weight: grams,
  //       metalName: metal?.metalName,
  //       amount: totalAmount,
  //       buyType: "Weight",
  //       transactionMode: selectedGateway,
  //       planid: "",
  //       moneysip: false,
  //       x_api_version: "2022-09-01",
  //       jeweller: process.env.REACT_APP_JEWELLER_ID,
  //     };
  //     dispatch(verifyOnlineOrderRequest(verifyPayload));

  //     setLoading(false);
  //   } catch (err) {
  //     console.error(err);
  //     toast.error("Payment verification failed", "error");
  //     setLoading(false);
  //   }
  // };

  const handlePayOffline = async () => {
    try {
      setLoading(true);

      const offlineOrderPayload = {
        weight: grams,
        metalName: metal?.metalName,
        orderType: "Buy",
        tax: tax.toFixed(decimal),
        final_amount: finalAmount.toFixed(decimal),
        noOfMonths: 0,
        Id: "",
        sipDay: 0,
        buyType: "Weight",
        purity: metal?.purity,
        x_api_version: "2022-09-01",
      };

      dispatch(createOfflineOrderRequest(offlineOrderPayload));
    } catch (err) {
      console.error(err);
      toast.error("Offline payment failed", "error");
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(resetOnlineOrderState());
    return () => {
      dispatch(resetOnlineOrderState());
    };
  }, []);

  useEffect(() => {
    if (onlineOrder.error) {
      toast.error(
        onlineOrder.error?.message ||
          onlineOrder.error ||
          "Order creation failed",
      );
      setLoading(false);
    }
  }, [onlineOrder.error]);

  useEffect(() => {
    if (!onlineOrder.data || !selectedGateway) return;

    const orderResponse = onlineOrder.data;
    const pay = async () => {
      setLoading(true);
      localStorage.setItem("online_order_data", JSON.stringify(orderResponse));
      await startPaymentFromOrderResponse(orderResponse, selectedGateway, {
        autodebit: autoDebit,
      });
      setLoading(false);
    };
    pay();
  }, [onlineOrder.data, selectedGateway, autoDebit]);

  // useEffect(() => {
  //   if (!onlineOrder.verifyData) return;

  //   const verifyRes = onlineOrder.verifyData;

  //   if (verifyRes.success) {
  //     navigate("/payment-status", {
  //       state: {
  //         isSuccess: true,
  //         orderId: verifyRes.orderId,
  //         metalName: metal?.metalName,
  //         metalPurity: `${metal?.karat} ${metal?.purity}`,
  //         amount: totalAmount,
  //         invoiceUrl: `${process.env.REACT_APP_INVOICE_URL}/api/v1/transactions/getInvoice/${verifyRes.data}`,
  //       },
  //     });
  //   } else {
  //     navigate("/payment-status", {
  //       state: {
  //         isSuccess: false,
  //         orderId: verifyRes.orderId,
  //         metalName: metal?.metalName,
  //         metalPurity: `${metal?.karat} ${metal?.purity}`,
  //         amount: totalAmount,
  //       },
  //     });
  //   }

  //   setLoading(false);
  // }, [onlineOrder.verifyData]);

  useEffect(() => {
    if (offlineOrder.data?.orderId) {
      navigate("/offline-payment", {
        state: {
          ...state,
          offlineOrderData: {
            orderId: offlineOrder.data.orderId,
            metalName: metal.metalName,
            karat: metal.karat,
            purity: metal.purity,
            weight: grams,
            tax: tax.toFixed(decimal),
            finalAmount: finalAmount.toFixed(decimal),
          },
        },
      });
      dispatch(resetOfflineOrderState());
      setLoading(false);
    }

    if (offlineOrder.error) {
      setLoading(false);
    }
  }, [offlineOrder.data, offlineOrder.error]);

  const themeColors = useAppTheme();

  const { symbol } = useCurrency();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: themeColors.scaffoldGradientColors,
      }}
    >
      <Card
        sx={{
          width: "50vw",
          borderRadius: "25px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
          color: themeColors.text,
          p: 3,
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              color: themeColors.primary,
              mb: 3,
            }}
          >
            Order Summary
          </Typography>

          <Box
            sx={{
              background: metalColor,
              borderRadius: "15px",
              textAlign: "center",
              p: 2,
              mb: 3,
              boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "#fff", letterSpacing: 0.5 }}
            >
              {metal.metalName}- {metal.karat}k - {metal.purity}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 0.8,
            }}
          >
            <Typography sx={{ fontWeight: "bold" }}>Quantity</Typography>
            <Typography>{grams.toFixed(decimal)} grams</Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "16px",
              mb: 1,
            }}
          >
            Amount Details
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 0.8,
            }}
          >
            <Typography>Metal Cost</Typography>
            <Typography>{symbol}{amount.toFixed(decimal)}</Typography>
          </Box>

          <Typography
            sx={{
              fontSize: "12px",
              mb: 1,
            }}
          >
            ({symbol} {amount.toFixed(decimal)} * {grams.toFixed(decimal)} gms)
          </Typography>

          {taxtype === "GST" && isSameState && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.8,
                }}
              >
                <Typography>CGST ({cgstPercent}%)</Typography>
                <Typography>{symbol}{cgst}</Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography>SGST ({sgstPercent}%)</Typography>
                <Typography>{symbol}{sgst}</Typography>
              </Box>
            </>
          )}

          {taxtype === "GST" && !isSameState && (
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}
            >
              <Typography>IGST ({igstPercent}%)</Typography>
              <Typography>{symbol}{tax.toFixed(decimal)}</Typography>
            </Box>
          )}

          {taxtype === "VAT" && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.8,
                }}
              >
                <Typography>TAX ({igstPercent}%)</Typography>
                <Typography>
                  {symbol}{((metalCost * igstPercent) / 100).toFixed(decimal)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography>CESS ({cessPercent}%)</Typography>
                <Typography>
                  {symbol}
                  {(
                    (((metalCost * igstPercent) / 100) * cessPercent) /
                    100
                  ).toFixed(decimal)}
                </Typography>
              </Box>
            </>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Typography sx={{ fontWeight: "bold" }}>Total Amount</Typography>
            <Typography sx={{ fontWeight: "bold" }}>
              {symbol}{finalAmount.toFixed(decimal)}
            </Typography>
          </Box>
          <Divider sx={{ my: 1.5 }} />

          <Typography
            sx={{
              fontSize: "14px",
              mb: 1,
            }}
          >
            Disclaimer : Quantity bought cannot be sold or gifted for next 72
            hrs
          </Typography>

          {buyAutoDebitEnabled && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                background: themeColors.mediumContainer,
                color: themeColors.text,
                border: `1px solid ${themeColors.scaffold}`,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoDebit}
                    onChange={(e) => setAutoDebit(e.target.checked)}
                    sx={{ color: themeColors.text }}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontWeight: "bold" }}>
                      Auto Debit
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      By opting for Auto Debit, future payments will be auto
                      collected on the selected interval.
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 2,
            }}
          >
            {paymentModeEnabled.online == true && (
              <Button
                fullWidth
                variant="contained"
                sx={{
                  background: themeColors.primary,
                  color: themeColors.text,
                  borderRadius: "25px",
                  py: 1.2,
                  fontWeight: "bold",
                  "&:hover": {
                    background: themeColors.svgColor,
                  },
                  border: `1px solid ${themeColors.scaffold}`,
                }}
                onClick={handlePayOnline}
                disabled={loading}
              >
                {loading ? "Processing..." : "Pay Online"}
              </Button>
            )}

            {paymentModeEnabled.offline == true && !autoDebit && (
              <Button
                fullWidth
                variant="contained"
                sx={{
                  background: themeColors.primary,
                  color: themeColors.text,
                  borderRadius: "25px",
                  py: 1.2,
                  fontWeight: "bold",
                  "&:hover": {
                    background: themeColors.svgColor,
                  },
                  border: `1px solid ${themeColors.scaffold}`,
                }}
                onClick={handlePayOffline}
              >
                Pay Offline
              </Button>
            )}
          </Box>

          <GatewaySelectionDialog
            open={openGatewayDialog}
            onClose={handleGatewaySelect}
            gateways={paymentGateways}
          />
          <AutoDebitIntervalDialog
            open={openIntervalDialog}
            onClose={handleIntervalSelect}
            config={appConfig?.buyAutoDebit}
          />

          <Button
            fullWidth
            sx={{
              mt: 2,
              borderRadius: "25px",
              background: themeColors.primary,
              color: themeColors.text,
              fontWeight: "bold",
              "&:hover": {
                background: themeColors.svgColor,
              },
              border: `1px solid ${themeColors.scaffold}`,
            }}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrderSummary;
